import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  CircleLoader,
  Icon,
  Space,
  Text,
  TextButton,
  TransparentIconButton,
} from '@procurify/ui'
import { FormattedMessage } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  ComposerBoxStyled,
  ComposerSendButtonStyled,
  ComposerTextareaStyled,
  SendErrorTextStyled,
} from './styles/MessageThreadComposerStyled'
import {
  ThreadHeaderRowStyled,
  ThreadTitleStyled,
} from './styles/MessageThreadHeaderStyled'
import {
  MessageBubbleStyled,
  MessageGroupStyled,
  MessageMetaAuthorStyled,
  MessageMetaStyled,
  MessageMetaTimestampStyled,
  MessageRowStyled,
  SystemEventMarkerStyled,
} from './styles/MessageThreadMessageStyled'
import {
  NoticeBannerIconStyled,
  NoticeBannerStyled,
} from './styles/MessageThreadNoticeStyled'
import {
  CenteredStateStyled,
  EmptyStateBubbleStyled,
  EmptyStateStyled,
} from './styles/MessageThreadStateStyled'
import {
  ScrollToLatestButtonStyled,
  ThreadColumnStyled,
  ThreadEdgeShadowStyled,
  ThreadScrollStyled,
  ThreadViewportStyled,
} from './styles/MessageThreadThreadStyled'

/** `own` sits on the trailing edge; `system` renders as an event marker. */
export type ThreadMessageAuthor = 'own' | 'other' | 'system'

export interface IThreadMessage {
  id: string
  body: string
  createdAt: Date
  author: ThreadMessageAuthor
  authorLabel: Nullable<string>
}

export interface IMessageThreadComposer {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  maxLength: number
  isSending: boolean
  disabled: boolean
  error?: string
  /** Defaults to a generic prompt when the consumer has no name to give. */
  placeholder?: string
}

export interface IMessageThreadProps {
  title?: string
  closeLabel?: string
  onClose?: () => void
  /** Shown between the header and the thread; omit to hide the banner. */
  notice?: ReactNode
  messages: IThreadMessage[]
  isLoading?: boolean
  /** Replaces the thread. Without `onRetry` the failure reads as final. */
  loadError?: { message: string; onRetry?: () => void }
  emptyState: { title: string; hint?: string }
  /** Omit to hide the composer altogether. */
  composer?: IMessageThreadComposer
}

export const MessageThread = ({
  title,
  closeLabel,
  onClose,
  notice,
  messages: thread,
  isLoading = false,
  loadError,
  emptyState,
  composer,
}: IMessageThreadProps) => {
  const intl = useProcurifyIntl()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ above: false, below: false })

  const updateEdges = useCallback(() => {
    const node = scrollRef.current
    if (!node) return
    const above = node.scrollTop > 1
    const below = node.scrollTop + node.clientHeight < node.scrollHeight - 1
    setEdges((previous) =>
      previous.above === above && previous.below === below
        ? previous
        : { above, below }
    )
  }, [])

  useLayoutEffect(() => {
    const node = scrollRef.current
    if (node) {
      node.scrollTop = node.scrollHeight
    }
    updateEdges()
  }, [thread.length, updateEdges])

  const scrollToLatest = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }

  const formatTimestamp = (date: Date) =>
    intl
      .formatDate(date, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
      .replace(/\s?(AM|PM)$/, (period) => period.trim().toLowerCase())

  const renderThread = () => {
    if (isLoading) {
      return (
        <CenteredStateStyled>
          <CircleLoader />
        </CenteredStateStyled>
      )
    }

    if (loadError) {
      return (
        <CenteredStateStyled>
          <div role='alert'>
            <Text>{loadError.message}</Text>
          </div>
          {loadError.onRetry && (
            <TextButton onClick={loadError.onRetry}>
              <FormattedMessage {...messages.retryLoad} />
            </TextButton>
          )}
        </CenteredStateStyled>
      )
    }

    if (thread.length === 0) {
      return (
        <EmptyStateStyled>
          <EmptyStateBubbleStyled>
            <Icon icon='message' size={28} aria-hidden />
          </EmptyStateBubbleStyled>
          <Text bold>{emptyState.title}</Text>
          {emptyState.hint && <Text>{emptyState.hint}</Text>}
        </EmptyStateStyled>
      )
    }

    return (
      <ThreadViewportStyled>
        <ThreadScrollStyled ref={scrollRef} onScroll={updateEdges}>
          {thread.map((message) => {
            const timestamp = formatTimestamp(message.createdAt)

            if (message.author === 'system') {
              return (
                <SystemEventMarkerStyled key={message.id}>
                  {message.body} — {timestamp}
                </SystemEventMarkerStyled>
              )
            }

            const own = message.author === 'own'
            const timestampLabel = (
              <MessageMetaTimestampStyled>
                {timestamp}
              </MessageMetaTimestampStyled>
            )
            return (
              <MessageRowStyled key={message.id} $own={own} data-own={own}>
                <MessageGroupStyled $own={own}>
                  <MessageMetaStyled>
                    {message.authorLabel ? (
                      <FormattedMessage
                        {...messages.messageMeta}
                        values={{
                          author: (
                            <MessageMetaAuthorStyled>
                              {message.authorLabel}
                            </MessageMetaAuthorStyled>
                          ),
                          timestamp: timestampLabel,
                        }}
                      />
                    ) : (
                      timestampLabel
                    )}
                  </MessageMetaStyled>
                  <MessageBubbleStyled $own={own}>
                    {message.body}
                  </MessageBubbleStyled>
                </MessageGroupStyled>
              </MessageRowStyled>
            )
          })}
        </ThreadScrollStyled>
        {edges.above && <ThreadEdgeShadowStyled $edge='top' />}
        {edges.below && <ThreadEdgeShadowStyled $edge='bottom' />}
        {edges.below && (
          <ScrollToLatestButtonStyled
            type='button'
            onClick={scrollToLatest}
            aria-label={intl.formatMessage(messages.scrollToLatest)}
          >
            <Icon icon='chevron-thick-down' size={14} aria-hidden />
          </ScrollToLatestButtonStyled>
        )}
      </ThreadViewportStyled>
    )
  }

  const renderComposer = () => {
    if (!composer) return null

    const locked = composer.disabled || composer.isSending
    const placeholder =
      composer.placeholder ?? intl.formatMessage(messages.inputPlaceholder)

    return (
      <Space size='xsmall'>
        {composer.error && (
          <SendErrorTextStyled role='alert'>
            {composer.error}
          </SendErrorTextStyled>
        )}
        <ComposerBoxStyled>
          <ComposerTextareaStyled
            aria-label={placeholder}
            placeholder={placeholder}
            maxLength={composer.maxLength}
            value={composer.value}
            onChange={(event) => composer.onChange(event.target.value)}
            disabled={locked}
          />
          <ComposerSendButtonStyled
            type='button'
            disabled={locked || composer.value.trim() === ''}
            aria-label={intl.formatMessage(messages.send)}
            onClick={composer.onSend}
          >
            <Icon icon='pointer-up' size={16} aria-hidden />
          </ComposerSendButtonStyled>
        </ComposerBoxStyled>
      </Space>
    )
  }

  return (
    <ThreadColumnStyled>
      {title && (
        <ThreadHeaderRowStyled>
          <ThreadTitleStyled forwardedAs='h3'>{title}</ThreadTitleStyled>
          <TransparentIconButton
            onClick={onClose}
            aria-label={closeLabel}
            iconComponent={<Icon icon='close' size={16} aria-hidden />}
          />
        </ThreadHeaderRowStyled>
      )}

      {notice && (
        <NoticeBannerStyled
          icon={
            <NoticeBannerIconStyled>
              <Icon icon='notification' size={18} aria-hidden />
            </NoticeBannerIconStyled>
          }
        >
          {notice}
        </NoticeBannerStyled>
      )}

      {renderThread()}

      {renderComposer()}
    </ThreadColumnStyled>
  )
}

MessageThread.displayName = 'MessageThread'
